'use client';
import { useIntl, type UserRequirement, useSetPageTitle } from '@capsicum/common';
import { Button, CountrySelect, Skeleton, SkeletonItem, TextInput, useInfoModal } from '@capsicum/common-web';
import { useTurkishExtraFields } from '@capsicum/features/regulations/shared';
import { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

export type TurkishExtraFieldsProps = {
	isForcedRequirement?: boolean;
	onDismiss: (callback?: () => void) => void;
	requirement: UserRequirement;
};

export const TurkishExtraFields = ({ isForcedRequirement, onDismiss, requirement }: TurkishExtraFieldsProps) => {
	const { $t } = useIntl();
	const { canUpdateAddress, handleSubmitForm, isFormLoading, isSubmitLoading, turkishExtraFieldsForm } =
		useTurkishExtraFields({
			requirement
		});
	const [showModal, setShowModal] = useState(true);

	const onVerifyPress = () => setShowModal(false);

	const { showInfoModal } = useInfoModal();

	useSetPageTitle($t('turkishExtraFields.headerTitle'));

	useEffect(
		() =>
			showInfoModal({
				buttonOnHandlers: {
					onPrimaryButtonHandler: onVerifyPress,
					primaryInvokeOnDismiss: false
				},
				buttonTexts: {
					primaryButtonText: $t('requirements.common.modal.button')
				},
				image: 'questionnaire',
				isClosable: !isForcedRequirement,
				onDismiss,
				subtitle: $t('requirements.common.modal.description'),
				testID: 'turkish-extra-fields-modal',
				title: $t('requirements.common.modal.title'),
				variant: 'confirm'
			}),
		[]
	);

	if (showModal) return <S.EmptyContainer />;

	return (
		<S.Wrapper>
			<S.MainContentContainer>
				<FormProvider {...turkishExtraFieldsForm}>
					<S.BlockContainer>
						<S.BlockTitle>{$t('turkishExtraFields.block1Title')}</S.BlockTitle>
						<S.InputsContainer>
							{isFormLoading ? (
								<S.SkeletonWrapper height={56}>
									{Array.from({ length: 2 }, (_, i) => (
										<S.SkeletonInput key={i} />
									))}
								</S.SkeletonWrapper>
							) : (
								<>
									<TextInput
										label={$t('turkishExtraFields.form.cityOfBirth.label')}
										name="cityOfBirth"
										placeholder={$t('turkishExtraFields.form.cityOfBirth.placeholder')}
										readonly={isSubmitLoading}
										testID="turkish-extra-fields-city-of-birth-input"
									/>
									<CountrySelect
										label={$t('turkishExtraFields.form.countryOfBirth.label')}
										labelProp="name"
										name="countryOfBirth"
										placeholder={$t('turkishExtraFields.form.countryOfBirth.placeholder')}
										readonly={isSubmitLoading}
										searchPlaceholder={$t(
											'turkishExtraFields.form.countryOfBirth.searchPlaceholder'
										)}
										testID="turkish-extra-fields-country-of-birth-input"
										title={$t('turkishExtraFields.form.countryOfBirth.listTitle')}
										withoutInitialValue
										withSearch
									/>
								</>
							)}
						</S.InputsContainer>
					</S.BlockContainer>
					<S.BlockContainer>
						<S.BlockTitle>{$t('turkishExtraFields.block2Title')}</S.BlockTitle>
						<S.InputsContainer>
							{isFormLoading ? (
								<S.SkeletonWrapper height={56}>
									{Array.from({ length: 5 }, (_, i) => (
										<S.SkeletonInput key={i} />
									))}
								</S.SkeletonWrapper>
							) : (
								<>
									<TextInput
										disabled={!canUpdateAddress}
										label={$t('turkishExtraFields.form.street.label')}
										name="street"
										placeholder={$t('turkishExtraFields.form.street.placeholder')}
										readonly={isSubmitLoading}
										testID="turkish-extra-fields-street-input"
									/>
									<TextInput
										disabled={!canUpdateAddress}
										label={$t('turkishExtraFields.form.postcode.label')}
										name="zipCode"
										placeholder={$t('turkishExtraFields.form.postcode.placeholder')}
										readonly={isSubmitLoading}
										testID="turkish-extra-fields-zip-code-input"
									/>
									<TextInput
										disabled={!canUpdateAddress}
										label={$t('turkishExtraFields.form.city.label')}
										name="city"
										placeholder={$t('turkishExtraFields.form.city.placeholder')}
										readonly={isSubmitLoading}
										testID="turkish-extra-fields-city-input"
									/>
									<TextInput
										disabled={!canUpdateAddress}
										label={$t('turkishExtraFields.form.region.label')}
										name="region"
										placeholder={
											!canUpdateAddress ? '' : $t('turkishExtraFields.form.region.placeholder')
										}
										readonly={isSubmitLoading}
										testID="turkish-extra-fields-region-input"
									/>
									<CountrySelect
										disabled={!canUpdateAddress}
										label={$t('turkishExtraFields.form.country.label')}
										labelProp="name"
										name="country"
										placeholder={$t('turkishExtraFields.form.country.placeholder')}
										readonly={isSubmitLoading}
										searchPlaceholder={$t('turkishExtraFields.form.country.searchPlaceholder')}
										testID="turkish-extra-fields-country-input"
										title={$t('turkishExtraFields.form.country.listTitle')}
										withoutInitialValue
										withSearch
									/>
								</>
							)}
						</S.InputsContainer>
					</S.BlockContainer>
					<S.BlockContainer>
						<S.BlockTitle>{$t('turkishExtraFields.block3Title')}</S.BlockTitle>
						<S.InputsContainer>
							{isFormLoading ? (
								<S.SkeletonWrapper height={56}>
									{Array.from({ length: 2 }, (_, i) => (
										<S.SkeletonInput key={i} />
									))}
								</S.SkeletonWrapper>
							) : (
								<>
									<TextInput
										label={$t('turkishExtraFields.form.parent1.label')}
										name="parent1FullName"
										placeholder={$t('turkishExtraFields.form.parent1.placeholder')}
										readonly={isSubmitLoading}
										testID="turkish-extra-fields-parent1-full-name-input"
									/>
									<TextInput
										label={$t('turkishExtraFields.form.parent2.label')}
										name="parent2FullName"
										placeholder={$t('turkishExtraFields.form.parent2.placeholder')}
										readonly={isSubmitLoading}
										testID="turkish-extra-fields-parent2-full-name-input"
									/>
								</>
							)}
							<S.ButtonsContainer>
								<Button
									disabled={isFormLoading || !turkishExtraFieldsForm.formState.isValid}
									fullWidth
									isLoading={isSubmitLoading}
									testID="turkish-extra-fields-confirm-button"
									text={$t('global.button.submit')}
									onClick={handleSubmitForm}
								/>
								<Button
									color="neutral"
									disabled={isSubmitLoading}
									fullWidth
									testID="turkish-extra-fields-cancel-button"
									text={$t('global.button.cancel')}
									variant="text"
									onClick={() => onDismiss()}
								/>
							</S.ButtonsContainer>
						</S.InputsContainer>
					</S.BlockContainer>
				</FormProvider>
			</S.MainContentContainer>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: ${({ theme }) => `${theme.spacings.medium} 0 51px`};
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => `${theme.spacings.medium} ${theme.spacings.small} 51px`};
		}
	`;

	export const MainContentContainer = styled.div`
		display: flex;
		flex-direction: column;
		row-gap: ${({ theme }) => theme.spacings.xlarge};
	`;

	export const BlockContainer = styled.div`
		display: flex;
		flex-direction: column;
		row-gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const BlockTitle = styled.span`
		${({ theme }) => theme.fonts.headings.title5};
	`;

	export const InputsContainer = styled.div`
		display: flex;
		flex-direction: column;
		row-gap: ${({ theme }) => theme.spacings.xxsmall};
		padding: ${({ theme }) => `${theme.spacings.large} ${theme.spacings.small}`};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.medium};
	`;

	export const ButtonsContainer = styled.div`
		display: flex;
		flex-direction: column;
		row-gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const EmptyContainer = styled.div`
		flex: 1;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
	`;

	export const SkeletonWrapper = styled(Skeleton)`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const SkeletonInput = styled(SkeletonItem)`
		border-radius: ${({ theme }) => theme.sizing.xxsmall};
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
	`;
}
